/**
 * 에러 처리 관련 유틸리티 함수
 * 
 * API 에러를 일관되게 처리하고 사용자 친화적인 메시지를 제공합니다.
 */


/**
 * API 에러 응답 타입
 */
interface APIErrorResponse {
  detail?: string;
  message?: string;
  /**
   * 우리 서버는 `{"error": {"code", "message"}}` 로 답한다 (main.py 의 예외 핸들러).
   * 예전에는 여기를 문자열로만 보고 **객체를 그대로** 돌려줬다 — 그 값을 화면에 넣으면
   * "[object Object]" 가 보이거나 React 가 "Objects are not valid as a React child" 로
   * 터진다. 두 모양을 다 받는다.
   */
  error?: string | { code?: string; message?: string };
}

/**
 * API 에러에서 사용자 친화적인 메시지 추출
 * 
 * @param error - 에러 객체 (주로 AxiosError)
 * @param defaultMessage - 기본 에러 메시지
 * @returns 사용자에게 표시할 에러 메시지
 * 
 * @example
 * try {
 *   await api.call();
 * } catch (error) {
 *   const message = getErrorMessage(error, '요청에 실패했습니다.');
 *   setError(message);
 * }
 */
export const getErrorMessage = (
  error: any,
  defaultMessage: string
): string => {
  // Axios 에러인 경우
  if (error?.response?.data) {
    const data = error.response.data as APIErrorResponse;
    const nested = typeof data.error === 'object' ? data.error?.message : data.error;
    return data.detail || data.message || nested || defaultMessage;
  }
  
  // 일반 Error 객체인 경우
  if (error?.message) {
    return error.message;
  }
  
  // 문자열 에러인 경우
  if (typeof error === 'string') {
    return error;
  }
  
  return defaultMessage;
};

/**
 * API 에러를 처리하고 콘솔에 로그
 * 
 * @param error - 에러 객체
 * @param defaultMessage - 기본 에러 메시지
 * @param setError - 에러 상태 설정 함수 (옵션)
 * @returns 에러 메시지
 * 
 * @example
 * catch (error: any) {
 *   handleAPIError(error, '사용자 목록을 불러오는데 실패했습니다.', setError);
 * }
 */
export const handleAPIError = (
  error: any,
  defaultMessage: string,
  setError?: (message: string) => void
): string => {
  const message = getErrorMessage(error, defaultMessage);
  
  // 콘솔에 에러 로그 (개발 환경에서만)
  if (process.env.NODE_ENV === 'development') {
    console.error(`API Error: ${defaultMessage}`, {
      message,
      error,
      stack: error?.stack,
    });
  }
  
  // 에러 상태 설정
  if (setError) {
    setError(message);
  }
  
  return message;
};

/**
 * 에러 메시지가 특정 상태 코드인지 확인
 * 
 * @param error - 에러 객체
 * @param statusCode - 확인할 HTTP 상태 코드
 * @returns 해당 상태 코드면 true
 */
export const isErrorStatus = (error: any, statusCode: number): boolean => {
  return error?.response?.status === statusCode;
};

/**
 * 인증 에러인지 확인 (401)
 * 
 * @param error - 에러 객체
 * @returns 인증 에러면 true
 */
export const isAuthError = (error: any): boolean => {
  return isErrorStatus(error, 401);
};

/**
 * 권한 에러인지 확인 (403)
 * 
 * @param error - 에러 객체
 * @returns 권한 에러면 true
 */
export const isForbiddenError = (error: any): boolean => {
  return isErrorStatus(error, 403);
};

/**
 * Not Found 에러인지 확인 (404)
 * 
 * @param error - 에러 객체
 * @returns Not Found 에러면 true
 */
export const isNotFoundError = (error: any): boolean => {
  return isErrorStatus(error, 404);
};

/**
 * 에러를 사용자 친화적인 메시지로 변환
 * 특정 에러 케이스에 대한 맞춤 메시지 제공
 * 
 * @param error - 에러 객체
 * @param context - 에러 발생 컨텍스트
 * @returns 사용자 친화적인 에러 메시지
 */
export const getUserFriendlyErrorMessage = (
  error: any,
  context: 'fetch' | 'create' | 'update' | 'delete' = 'fetch'
): string => {
  if (isAuthError(error)) {
    return '로그인이 필요합니다. 다시 로그인해주세요.';
  }
  
  if (isForbiddenError(error)) {
    return '이 작업을 수행할 권한이 없습니다.';
  }
  
  if (isNotFoundError(error)) {
    return '요청한 리소스를 찾을 수 없습니다.';
  }
  
  const contextMessages: Record<typeof context, string> = {
    fetch: '데이터를 불러오는데 실패했습니다.',
    create: '생성에 실패했습니다.',
    update: '수정에 실패했습니다.',
    delete: '삭제에 실패했습니다.',
  };
  
  return getErrorMessage(error, contextMessages[context]);
};

// 하위 호환성을 위한 별칭
export const extractErrorMessage = getErrorMessage;

/**
 * Try-catch 블록을 더 깔끔하게 작성하기 위한 헬퍼
 * 
 * @param fn - 실행할 비동기 함수
 * @param errorHandler - 에러 처리 함수
 * @returns Promise<void>
 * 
 * @example
 * await withErrorHandling(
 *   () => api.fetchUsers(),
 *   (error) => setError(extractErrorMessage(error, '사용자 목록 조회 실패'))
 * );
 */
export const withErrorHandling = async <T>(
  fn: () => Promise<T>,
  errorHandler: (error: any) => void
): Promise<T | undefined> => {
  try {
    return await fn();
  } catch (error) {
    errorHandler(error);
    return undefined;
  }
};
